import {
    Component,
    signal,
    inject,
    ViewChild,
    ElementRef,
    AfterViewChecked,
    OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { ChatService, ChatMessage } from './chat.service';

interface Welcome { title: string; body: string; news: string; more: string; placeholder: string; clear: string; }

/** The first thing the assistant says, in the language the browser asks for. */
const WELCOME: Record<string, Welcome> = {
    en: { title: 'Welcome to Altura Assistant.', body: 'Ask me anything about your flights, aircraft or crew.', news: 'New: you can connect your own AI (Claude, ChatGPT or Gemini) to Altura.', more: 'Learn more', placeholder: 'Type a message...', clear: 'Clear chat' },
    es: { title: 'Le damos la bienvenida a Altura Assistant.', body: 'Pregúnteme lo que quiera sobre sus vuelos, aviones o tripulación.', news: 'Novedad: puede conectar su propia IA (Claude, ChatGPT o Gemini) a Altura.', more: 'Más información', placeholder: 'Escriba un mensaje...', clear: 'Limpiar el chat' },
    pt: { title: 'Bem-vindo ao Altura Assistant.', body: 'Pergunte-me o que quiser sobre os seus voos, aeronaves ou tripulação.', news: 'Novidade: pode ligar a sua própria IA (Claude, ChatGPT ou Gemini) à Altura.', more: 'Saber mais', placeholder: 'Escreva uma mensagem...', clear: 'Limpar a conversa' },
    fr: { title: 'Bienvenue sur Altura Assistant.', body: 'Posez-moi vos questions sur vos vols, vos appareils ou votre équipage.', news: 'Nouveau : vous pouvez connecter votre propre IA (Claude, ChatGPT ou Gemini) à Altura.', more: 'En savoir plus', placeholder: 'Écrivez un message...', clear: 'Effacer la conversation' },
};


@Component({
    selector: 'chat-widget',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
    ],
    template: `
        <!-- Floating button -->
        @if (!isOpen()) {
            <button
                class="chat-fab"
                (click)="toggle()"
                matTooltip="Chat with Altura Assistant">
                <img src="images/logo/logo-on-dark.svg" class="chat-fab-logo" alt="Altura" />
            </button>
        }

        <!-- Chat panel -->
        @if (isOpen()) {
            <div class="chat-panel" [class.chat-panel-expanded]="isExpanded()">
                <!-- Header -->
                <div class="chat-header">
                    <div class="chat-header-info">
                        <img src="images/logo/logo-on-dark.svg" class="chat-header-logo" alt="Altura" />
                        <span class="chat-header-title">Altura Assistant</span>
                    </div>
                    <div class="chat-header-actions">
                        @if (messages().length > 0) {
                            <button mat-icon-button (click)="clearChat()" [matTooltip]="words.clear">
                                <mat-icon class="text-white">delete_sweep</mat-icon>
                            </button>
                        }
                        <button mat-icon-button (click)="toggleExpand()" [matTooltip]="isExpanded() ? 'Minimize' : 'Expand'">
                            <mat-icon class="text-white">{{ isExpanded() ? 'close_fullscreen' : 'open_in_full' }}</mat-icon>
                        </button>
                        <button mat-icon-button (click)="toggle()" matTooltip="Close">
                            <mat-icon class="chat-close-icon">close</mat-icon>
                        </button>
                    </div>
                </div>

                <!-- Messages -->
                <div class="chat-messages" #messagesContainer>
                    @if (messages().length === 0) {
                        <div class="chat-empty">
                            <img src="images/logo/logo-dark.svg" class="chat-empty-logo" alt="Altura" />
                            <p class="chat-empty-text">{{ words.title }}</p>
                            <p class="chat-empty-subtext">{{ words.body }}</p>
                            <p class="chat-empty-news">
                                {{ words.news }}
                                <a class="chat-empty-link" href="/mcp" (click)="openMcp($event)">{{ words.more }}</a>
                            </p>
                        </div>
                    }

                    @for (msg of messages(); track msg.id) {
                        <div class="chat-message" [class.chat-message-user]="msg.role === 'user'"
                             [class.chat-message-assistant]="msg.role === 'assistant'">
                            @if (msg.role === 'assistant') {
                                <div class="chat-avatar">
                                    <img src="images/logo/logo-dark.svg" alt="A" />
                                </div>
                            }
                            <div class="chat-bubble"
                                 [class.chat-bubble-user]="msg.role === 'user'"
                                 [class.chat-bubble-assistant]="msg.role === 'assistant'">
                                @if (msg.status === 'pending') {
                                    <mat-spinner diameter="20"></mat-spinner>
                                } @else {
                                    <div class="chat-bubble-content" [innerHTML]="formatMessage(msg.content)"></div>
                                }
                            </div>
                        </div>
                    }

                    @if (isSending()) {
                        <div class="chat-message chat-message-assistant">
                            <div class="chat-avatar">
                                <img src="images/logo/logo-dark.svg" alt="A" />
                            </div>
                            <div class="chat-bubble chat-bubble-assistant">
                                <div class="chat-typing">
                                    <span></span><span></span><span></span>
                                </div>
                            </div>
                        </div>
                    }
                </div>

                <!-- Input -->
                <div class="chat-input-container">
                    <input
                        #chatInput
                        class="chat-input"
                        [(ngModel)]="inputText"
                        (keydown.enter)="sendMessage()"
                        [placeholder]="words.placeholder"
                        autocomplete="off"
                    />
                    <button
                        class="chat-send-btn"
                        (click)="sendMessage()"
                        [disabled]="!inputText.trim() || isSending()">
                        <mat-icon>send</mat-icon>
                    </button>
                </div>
            </div>
        }
    `,
    styles: [`
        :host {
            position: fixed;
            bottom: 24px;
            right: 24px;
            z-index: 9999;
        }

        @media (max-width: 639px) {
            :host {
                bottom: 88px;
            }
        }

        .chat-fab {
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background: linear-gradient(135deg, #4f46e5, #6366f1);
            color: white;
            border: none;
            cursor: pointer;
            box-shadow: 0 4px 14px rgba(79, 70, 229, 0.4);
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.2s, box-shadow 0.2s;
        }
        .chat-fab:hover {
            transform: scale(1.08);
            box-shadow: 0 6px 20px rgba(79, 70, 229, 0.5);
        }
        .chat-fab-logo { width: 28px; height: 28px; }
        .chat-header-logo { width: 24px; height: 24px; }
        .chat-empty-logo { width: 48px; height: 48px; margin-bottom: 12px; opacity: 1; }

        .chat-panel {
            width: 380px;
            height: 520px;
            background: white;
            border-radius: 16px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
            display: flex;
            flex-direction: column;
            overflow: hidden;
            animation: slideUp 0.25s ease-out;
            transition: all 0.3s ease;
        }

        .chat-panel-expanded {
            position: fixed;
            top: 20px;
            bottom: 20px;
            right: 20px;
            width: calc(100% - 40px);
            height: auto;
            border-radius: 12px;
        }

        @media (min-width: 1200px) {
            .chat-panel-expanded {
                width: 50%;
                left: auto;
            }
        }

        @media (min-width: 1440px) {
            .chat-panel-expanded {
                width: 30%;
                left: auto;
            }
        }

        @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }

        .chat-header {
            background: linear-gradient(135deg, #4f46e5, #6366f1);
            padding: 14px 16px;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
        .chat-header-info {
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .chat-header-title {
            color: white;
            font-weight: 600;
            font-size: 15px;
        }
        .chat-header-actions {
            display: flex;
            gap: 0;
        }
        .chat-header-actions button {
            width: 32px;
            height: 32px;
        }
        .chat-header-actions mat-icon {
            color: white !important;
            font-size: 20px;
        }
        .chat-close-icon {
            font-weight: 700 !important;
            font-size: 22px !important;
        }

        .chat-messages {
            flex: 1;
            overflow-y: auto;
            padding: 16px;
            display: flex;
            flex-direction: column;
            gap: 12px;
            background: #f8fafc;
        }

        .chat-empty {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            color: #94a3b8;
        }
        .chat-empty-text { font-size: 15px; font-weight: 500; color: #475569; margin: 0; }
        .chat-empty-subtext { font-size: 13px; margin: 4px 0 0; }
        .chat-empty-news { font-size: 13px; margin: 14px 0 0; padding: 10px 12px; border-radius: 10px; background: #eef2ff; color: #3730a3; text-align: left; }
        .chat-empty-link { font-weight: 600; text-decoration: underline; cursor: pointer; color: inherit; }

        .chat-message {
            display: flex;
            gap: 8px;
            max-width: 85%;
        }
        .chat-message-user {
            align-self: flex-end;
            flex-direction: row-reverse;
        }
        .chat-message-assistant {
            align-self: flex-start;
        }

        .chat-avatar {
            width: 28px;
            height: 28px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        }
        .chat-avatar img {
            width: 28px;
            height: 28px;
            opacity: 1;
        }

        .chat-bubble {
            padding: 10px 14px;
            border-radius: 16px;
            font-size: 13px;
            line-height: 1.5;
            word-break: break-word;
        }
        .chat-bubble-user {
            background: #4f46e5;
            color: white;
            border-bottom-right-radius: 4px;
        }
        .chat-bubble-assistant {
            background: white;
            color: #1e293b;
            border: 1px solid #e2e8f0;
            border-bottom-left-radius: 4px;
        }
        .chat-bubble-content { white-space: pre-wrap; }

        .chat-typing {
            display: flex;
            gap: 4px;
            padding: 4px 0;
        }
        .chat-typing span {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: #94a3b8;
            animation: typing 1.2s infinite;
        }
        .chat-typing span:nth-child(2) { animation-delay: 0.2s; }
        .chat-typing span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes typing {
            0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
            40% { transform: scale(1); opacity: 1; }
        }

        .chat-input-container {
            display: flex;
            padding: 12px;
            gap: 8px;
            border-top: 1px solid #e2e8f0;
            background: white;
        }
        .chat-input {
            flex: 1;
            border: 1px solid #e2e8f0;
            border-radius: 24px;
            padding: 10px 16px;
            font-size: 13px;
            outline: none;
            transition: border-color 0.2s;
        }
        .chat-input:focus {
            border-color: #4f46e5;
        }
        .chat-input:disabled {
            background: #f1f5f9;
        }

        .chat-send-btn {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: #4f46e5;
            color: white;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background 0.2s;
        }
        .chat-send-btn:hover:not(:disabled) { background: #4338ca; }
        .chat-send-btn:disabled { background: #cbd5e1; cursor: not-allowed; }
        .chat-send-btn mat-icon { font-size: 18px; width: 18px; height: 18px; color: white; }
    `],
})
export class ChatWidgetComponent implements AfterViewChecked, OnDestroy {
    @ViewChild('messagesContainer') messagesContainer!: ElementRef;
    @ViewChild('chatInput') chatInput?: ElementRef<HTMLInputElement>;

    private chatService = inject(ChatService);
    private router = inject(Router);

    /** Browser language first; English when it is one we do not write in. */
    readonly words: Welcome = WELCOME[(navigator.language || 'en').slice(0, 2).toLowerCase()] ?? WELCOME['en'];

    /** The page with the whole explanation, without leaving the conversation behind. */
    openMcp(event: Event): void {
        event.preventDefault();
        this.isOpen.set(false);
        this.router.navigateByUrl('/mcp');
    }

    isOpen = signal(false);
    isExpanded = signal(false);
    messages = signal<ChatMessage[]>([]);
    isSending = signal(false);
    sessionId = signal<string | null>(null);
    inputText = '';

    private shouldScroll = false;
    private historyLoaded = false;

    toggle(): void {
        this.isOpen.update(v => !v);
        if (!this.isOpen()) {
            this.isExpanded.set(false);
        }
        if (this.isOpen() && !this.sessionId()) {
            this.startNewSession();
        }
    }

    toggleExpand(): void {
        this.isExpanded.update(v => !v);
    }

    /** Start from a clean screen. Nothing is deleted: the conversation stays on record. */
    clearChat(): void {
        this.chatService.clear().subscribe({
            next: () => {
                this.messages.set([]);
                this.sessionId.set(null);
                this.historyLoaded = true;
                this.startNewSession();
            },
        });
    }

    startNewSession(): void {
        this.chatService.newSession().subscribe({
            next: (resp) => {
                this.sessionId.set(resp.session_id);
                if (!this.historyLoaded) {
                    this.loadHistory();
                }
            },
        });
    }

    loadHistory(): void {
        this.chatService.getMessages().subscribe({
            next: (resp) => {
                const msgs: ChatMessage[] = [];
                for (const m of resp.messages || []) {
                    if (m.question) {
                        msgs.push({
                            id: m.asked_at || Date.now(),
                            role: 'user',
                            content: m.question,
                            status: 'sent',
                            created_at: m.asked_at_iso || '',
                        });
                    }
                    if (m.answer) {
                        msgs.push({
                            id: (m.asked_at || Date.now()) + 1,
                            role: 'assistant',
                            content: m.answer,
                            status: m.status === 'error' ? 'error' : 'delivered',
                            created_at: m.asked_at_iso || '',
                        });
                    }
                }
                this.messages.set(msgs);
                this.historyLoaded = true;
                this.shouldScroll = true;
            },
        });
    }

    /** The reply has arrived: put the cursor back so the next question can be typed straight away. */
    private focusInput(): void {
        setTimeout(() => this.chatInput?.nativeElement.focus());
    }

    sendMessage(): void {
        const text = this.inputText.trim();
        if (!text || this.isSending()) return;

        this.inputText = '';
        this.isSending.set(true);

        const tempUserMsg: ChatMessage = {
            id: Date.now(),
            role: 'user',
            content: text,
            status: 'sent',
            created_at: new Date().toISOString(),
        };
        this.messages.update(msgs => [...msgs, tempUserMsg]);
        this.shouldScroll = true;

        this.chatService.sendMessage(text, this.sessionId() || undefined).subscribe({
            next: (resp) => {
                if (!this.sessionId()) {
                    this.sessionId.set(resp.session_id);
                }

                const assistantMsg: ChatMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    content: resp.answer || 'No response received.',
                    status: resp.status === 'error' ? 'error' : 'delivered',
                    created_at: new Date().toISOString(),
                };
                this.messages.update(msgs => [...msgs, assistantMsg]);
                this.isSending.set(false);
                this.shouldScroll = true;
                this.focusInput();
            },
            error: (err) => {
                console.error('Chat send error:', err);
                const errorMsg: ChatMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    content: 'Unable to connect to the assistant. Please try again.',
                    status: 'error',
                    created_at: new Date().toISOString(),
                };
                this.messages.update(msgs => [...msgs, errorMsg]);
                this.isSending.set(false);
                this.shouldScroll = true;
                this.focusInput();
            },
        });
    }

    formatMessage(content: string): string {
        if (!content) return '';
        return content
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\n/g, '<br>');
    }

    ngAfterViewChecked(): void {
        if (this.shouldScroll && this.messagesContainer) {
            const el = this.messagesContainer.nativeElement;
            el.scrollTop = el.scrollHeight;
            this.shouldScroll = false;
        }
    }

    ngOnDestroy(): void {
        // cleanup if needed
    }
}
